import { Button } from '@/common/ui/button'
import { Dialog, DialogContent, DialogFooter } from '@/common/ui/dialogMobileSupport'
import { COMMON_COPY, EXIT_INTENT_COPY } from '@/locales/en'
import { H2 } from '@/common/ui/h2'
import { Checkbox } from '@/common/ui/checkbox'
import { useState, useEffect } from 'react'
import { useSubmitFeedback } from '@/user/userQueries'
import { FeedbackId } from '@/common/types'
import { toast } from 'sonner'
import { Errors } from '@/common/strings'
import UserFeedbackClosedAnalytics from '@/common/analytics/events/UserFeedbackClosedAnalytics'

export function BundleExitIntentDialog(props: { open: boolean; onClose: () => void; bundleId: string }) {
    const { open, onClose, bundleId } = props

    const { title, question, pickMany, options, otherPlaceholder } = EXIT_INTENT_COPY

    const [selected, setSelected] = useState<string[]>([])
    const [otherText, setOtherText] = useState('')

    const submitFeedbackMutation = useSubmitFeedback()

    const handleCancel = () => {
        submitFeedbackMutation.mutate({
            feedbackId: FeedbackId.BUNDLE_EXIT_INTENT,
        })
        UserFeedbackClosedAnalytics.track({ feedbackId: FeedbackId.BUNDLE_EXIT_INTENT, bundleId })
        onClose()
    }

    const handleSubmit = () => {
        if (!canSubmit()) return

        const response = selected.map((a) => {
            if (a === options[options.length - 1]) {
                return otherText ? `Other: ${otherText}` : 'Other'
            }
            return a
        })

        const payload = [
            {
                question,
                response,
            },
        ]

        submitFeedbackMutation.mutate({
            feedbackId: FeedbackId.BUNDLE_EXIT_INTENT,
            data: { questions: payload, referenceId: bundleId },
        })
    }

    useEffect(() => {
        if (!open) {
            setSelected([])
            setOtherText('')
        }
    }, [open])

    useEffect(() => {
        if (!submitFeedbackMutation.isSuccess) return

        onClose()
    }, [submitFeedbackMutation.isSuccess, onClose])

    useEffect(() => {
        if (!submitFeedbackMutation.isError) return
        toast.error(Errors.server)
    }, [submitFeedbackMutation.isError])

    const canSubmit = () => {
        const isOtherSelected = selected.includes(options[options.length - 1])
        return selected.length > 0 && (!isOtherSelected || otherText.trim().length > 0)
    }
    return (
        <Dialog
            open={open}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    handleCancel()
                }
            }}
        >
            <DialogContent className="mt-auto md:mt-0 max-w-[600px] h-fit p-4 md:px-10 md:py-0" onClose={handleCancel}>
                <div className="self-stretch flex flex-col justify-start items-center gap-6 pt-10 sm:pt-0 mb-20 sm:mb-0">
                    <H2 className="self-stretch justify-start text-black text-3xl font-normal font-['Serious Sans'] leading-10 mb-0">
                        {title}
                    </H2>
                    <div className="self-stretch flex flex-col justify-start items-start gap-4">
                        <div className="self-stretch inline-flex justify-start items-start gap-4 flex-wrap content-start">
                            <div className="justify-start text-black text-sm font-medium font-['Inter'] leading-5">
                                {question}
                            </div>
                            <div className="justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                                {pickMany}
                            </div>
                        </div>
                    </div>
                    <div className="self-stretch flex flex-col justify-start items-start gap-4">
                        <div className="self-stretch inline-flex flex-col justify-start items-start gap-4">
                            {options.map((opt) => {
                                const isChecked = selected.includes(opt)
                                const id = `exit-reason-${opt.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`
                                return (
                                    <div key={opt} className="inline-flex justify-start items-center gap-4">
                                        <Checkbox
                                            id={id}
                                            checked={isChecked}
                                            onCheckedChange={(e: boolean) => {
                                                if (e) setSelected((p) => (p.includes(opt) ? p : [...p, opt]))
                                                else setSelected((p) => p.filter((x) => x !== opt))
                                            }}
                                        />
                                        <label
                                            htmlFor={id}
                                            className="cursor-pointer text-center justify-start text-black text-sm font-normal font-['Inter'] leading-5"
                                        >
                                            {opt}
                                        </label>
                                    </div>
                                )
                            })}

                            {selected.includes(options[options.length - 1]) && (
                                <div
                                    data-helper-text="false"
                                    data-label="false"
                                    data-state="Default"
                                    data-type="default"
                                    className="w-full inline-flex flex-col justify-start items-start gap-1.5 mt-2"
                                >
                                    <div className="w-full flex flex-col justify-start items-start gap-2">
                                        <div className="w-full flex flex-col justify-start items-start gap-2">
                                            <textarea
                                                aria-label="Tell us what we missed"
                                                value={otherText}
                                                onChange={(e) => setOtherText(e.target.value)}
                                                placeholder={otherPlaceholder}
                                                className="w-full self-stretch h-20 px-3 py-2 bg-white rounded-md outline outline-1 outline-offset-[-1px] outline-border-outline resize-none"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <DialogFooter className="w-full px-4 py-3 shadow-[0px_4px_16px_0px_rgba(0,0,0,0.04)] border-t sm:shadow-none sm:border-none border-border-outline self-stretch sm:flex-col inline-flex justify-between sm:justify-between items-center">
                    <Button
                        variant="tertiary"
                        onClick={handleSubmit}
                        disabled={!canSubmit() || submitFeedbackMutation.isPending}
                        className="w-full sm:w-auto"
                    >
                        {EXIT_INTENT_COPY.submit || COMMON_COPY.submit}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
